import { useEffect, useRef, useState } from 'preact/hooks';
import { ChevronDown, ChevronRight } from 'lucide-preact';
import { api } from '@/lib/teaching/teacher-api';
import { useAssessmentId, invalidateDataset } from '@/lib/teaching/state';
import { errText, waitForJob, CONF_STYLE, type Job } from '@/lib/teaching/slice2';
import { FB_FIELDS, canDraft, feedbackStatus, shownText, type FbField, type FeedbackRowView, type FeedbackView } from '@/lib/teaching/slice3';
import { Card } from '@/components/teaching/ui';
import { inputCls } from '@/components/teaching/form';
import { Btn, ErrorLine } from '@/components/teaching/form';
import { Pill, SetupPage, Note, Tabs, textareaCls, NoAiNote } from '@/components/teaching/setup';
import { useAiReady, aiBlockText } from '@/lib/teaching/ai-ready';
import { MypTabs } from './Descriptors';

// Feedback drafting and approval (§05, SOP §38). The AI drafts four parts
// from approved evidence once a level is confirmed; the teacher edits any
// field and approves. A field the server flagged must be rewritten first.
// "Approve opened" only includes drafts the teacher expanded.

type Ctx = { a: string; reload: () => Promise<void> };

export function Feedback() {
  const a = useAssessmentId();
  const [v, setV] = useState<FeedbackView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [cls, setCls] = useState('all');
  const [open, setOpen] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => { if (a) setV(await api<FeedbackView>('GET', `/api/t/assessments/${a}/feedback`)); };
  useEffect(() => { setV(null); setErr(null); void reload().catch((e) => setErr(errText(e))); }, [a]);
  const changed = async () => { if (a) invalidateDataset(a); await reload(); };

  const classes = v ? Array.from(new Set(v.rows.map((r) => r.class_name))).map((c) => ({ id: c, label: c })) : [];
  const rows = v?.rows.filter((r) => cls === 'all' || r.class_name === cls) ?? [];
  const toDraft = rows.filter(canDraft);
  const bulk = rows.filter((r) => r.target && !r.bulk_block);
  const heldBack = rows.filter((r) => r.target && r.bulk_block);
  const running = job && (job.status === 'queued' || job.status === 'running');
  const ai = useAiReady();

  async function draft() {
    if (!a || !toDraft.length) return;
    setErr(null); setMsg(null);
    try {
      const r = await api<{ job: Job }>('POST', `/api/t/assessments/${a}/feedback/jobs`, { studentIds: toDraft.map((x) => x.student_id) });
      setJob(r.job);
      const done = await waitForJob(r.job.id, setJob);
      if (done.status === 'failed') setErr(`AI feedback failed: ${done.error ?? 'unknown error'}`);
      await reload();
    } catch (e) { setErr(errText(e)); }
  }
  async function approveOpened() {
    if (!a || !bulk.length) return;
    setErr(null); setMsg(null); setBusy(true);
    try {
      const r = await api<{ approved: number }>('POST', `/api/t/assessments/${a}/feedback/approve`, {
        mode: 'bulk', studentIds: bulk.map((x) => x.student_id), expectedRevisions: Object.fromEntries(bulk.map((x) => [x.student_id, x.feedback?.feedback_revision ?? 0])),
      });
      setMsg(`Approved ${r.approved} feedback draft${r.approved === 1 ? '' : 's'}.`);
      await changed();
    } catch (e) { setErr(errText(e)); await reload(); }
    setBusy(false);
  }
  async function toggle(r: FeedbackRowView) {
    if (open === r.student_id) { setOpen(null); return; }
    setOpen(r.student_id);
    // The exact proposal "Approve opened" would rely on (T4).
    const pid = r.target === 'proposal' ? r.proposal?.id : r.target === 'draft' ? r.feedback?.accepted_proposal_id : null;
    const seen = r.proposal && r.proposal.id === pid ? r.proposal.viewed : false;
    if (pid && !seen) { try { await api('POST', `/api/t/feedback-proposals/${pid}/viewed`); await reload(); } catch (e) { setErr(errText(e)); } }
  }

  const ctx: Ctx | null = a ? { a, reload: changed } : null;
  return (
    <SetupPage a={a} title="Feedback" actions={a && <MypTabs a={a} />}>
      <ErrorLine error={err} />
      {!v && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {v && ctx && (
        <>
          <Card title="Four-part feedback" sub="Drafted from each student's approved evidence and confirmed level. Nothing reaches a student until you approve it.">
            <div class="flex flex-wrap items-center gap-2">
              <Btn kind="primary" disabled={!!running || !toDraft.length || ai !== true} title={ai === false ? aiBlockText() : undefined} onClick={draft}>{running ? 'AI is drafting…' : `Draft feedback (${toDraft.length})`}</Btn>
              <Btn disabled={busy || !bulk.length} onClick={approveOpened}>{`Approve opened (${bulk.length})`}</Btn>
              {ai === false && <NoAiNote />}
            </div>
            <ul class="mt-2 text-[12px] text-[var(--color-text-muted)] flex flex-col gap-0.5">
              {heldBack.map((r) => <li key={r.student_id}>{r.name} held back: {r.bulk_block}.</li>)}
            </ul>
            {msg && <div class="mt-2"><Note tone="ok">{msg}</Note></div>}
          </Card>
          {classes.length > 1 && <Tabs label="Class" value={cls} onChange={setCls} options={[{ id: 'all', label: 'All' }, ...classes]} />}
          <div class="flex flex-col gap-2">
            {rows.map((r) => (
              <div key={r.student_id} data-student={r.name} class="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden min-w-0">
                <button type="button" onClick={() => toggle(r)} aria-expanded={open === r.student_id} aria-label={`Open feedback for ${r.name}`}
                  class="w-full text-left p-3 flex flex-wrap items-center gap-2 min-w-0" style={{ color: 'inherit' }}>
                  <span class="text-[var(--color-text-faint)]">{open === r.student_id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
                  <b class="text-[14px] text-[var(--color-text)]">{r.name}</b>
                  <span class="text-[11.5px] text-[var(--color-text-faint)]">{r.class_name}</span>
                  {r.judgement?.teacher_level != null && <span class="text-[12px] text-[var(--color-text-muted)]">Level {r.judgement.teacher_level}{r.judgement.status !== 'confirmed' ? ' (not confirmed)' : ''}</span>}
                  <span class="ml-auto flex flex-wrap items-center gap-1.5">
                    {r.proposal && r.target === 'proposal' && <Pill {...CONF_STYLE[r.proposal.confidence]} />}
                    {r.target && Object.keys(r.proposal?.field_reviews ?? {}).length > 0 && <Pill label="REVIEW FIELDS" bg="#fddcbf" />}
                    <Pill {...feedbackStatus(r)} />
                  </span>
                  
                </button>
                {open === r.student_id && <Detail ctx={ctx} r={r} />}
              </div>
            ))}
          </div>
        </>
      )}
    </SetupPage>
  );
}

function Detail({ ctx, r }: { ctx: Ctx; r: FeedbackRowView }) {
  const shown = shownText(r);
  const init = () => Object.fromEntries(FB_FIELDS.map((f) => [f.id, shown?.[f.id] ?? ''])) as Record<FbField, string>;
  const [text, setText] = useState<Record<FbField, string>>(init);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const rev = r.feedback?.feedback_revision ?? 0;
  // Reset only when the draft itself changes; a mount-time effect would
  // run after paint and could swallow the first edit.
  const key = `${rev}|${r.proposal?.id}|${r.target}`;
  const last = useRef(key);
  useEffect(() => { if (last.current !== key) { last.current = key; setText(init()); } }, [key]);

  const reviews = r.target === 'proposal' || (r.target === 'draft' && r.feedback?.accepted_proposal_id === r.proposal?.id) ? r.proposal?.field_reviews ?? {} : {};
  const teacherFields = new Set(r.target === 'draft' ? r.feedback?.teacher_fields ?? [] : []);
  const dirty = FB_FIELDS.some((f) => (text[f.id] ?? '') !== (shown?.[f.id] ?? ''));

  async function save() {
    setErr(null); setBusy(true);
    try {
      const changedFields = Object.fromEntries(FB_FIELDS.filter((f) => (text[f.id] ?? '') !== (shown?.[f.id] ?? '')).map((f) => [f.id, text[f.id]]));
      const body = r.target === 'proposal' && r.proposal ? { proposalId: r.proposal.id, ...changedFields } : changedFields;
      const out = await api<{ warnings: Record<string, string> }>('PUT', `/api/t/assessments/${ctx.a}/feedback/${r.student_id}`, body, rev);
      setWarn(out.warnings);
      await ctx.reload();
    } catch (e) { setErr(errText(e)); }
    setBusy(false);
  }
  async function approve() {
    setErr(null); setBusy(true);
    try {
      await api('POST', `/api/t/assessments/${ctx.a}/feedback/approve`, { mode: 'single', studentIds: [r.student_id], expectedRevisions: { [r.student_id]: rev } });
      await ctx.reload();
    } catch (e) { setErr(errText(e)); await ctx.reload(); }
    setBusy(false);
  }

  if (r.feedback?.status === 'not_applicable' && !r.target) return (
    <div class="px-3 pb-3 border-t border-[var(--color-border)] pt-3 text-[13px] text-[var(--color-text)]" data-feedback-na>
      <b>No feedback needed</b>: {r.feedback.not_applicable_reason}
    </div>
  );
  if (!shown) return (
    <div class="px-3 pb-3 flex flex-col gap-2">
      <div class="text-[13px] text-[var(--color-text-muted)]">No draft yet.{!r.ready ? ` Not ready yet: ${r.reasons.join('; ')}.` : ' Press Draft feedback above to get a first draft you can edit.'}</div>
      <NotApplicable ctx={ctx} r={r} />
    </div>
  );
  const approved = r.feedback?.status === 'approved' && !r.target;
  return (
    <div class="px-3 pb-3 flex flex-col gap-3 border-t border-[var(--color-border)] pt-3">
      {r.proposal?.review_reason && r.target === 'proposal' && <Note tone="bad">Review: {r.proposal.review_reason}. Rewrite every field before approving.</Note>}
      {r.feedback?.status === 'needs_review' && r.target === 'draft' && <Note tone="warn">Something it was based on changed (marks, level, descriptors, moderation or questions). Draft it again, or read it and save.</Note>}
      {FB_FIELDS.map((f) => (
        <label key={f.id} class="flex flex-col gap-1 text-[12px] text-[var(--color-text-muted)] min-w-0">
          <span class="flex flex-wrap items-center gap-1.5">
            {f.label}
            {teacherFields.has(f.id) && <span class="text-[11px] text-[var(--color-text-faint)]">your text</span>}
            {r.target === 'proposal' && (r.proposal?.field_refs?.[f.id]?.length ?? 0) > 0 && <span class="text-[11px] text-[var(--color-text-faint)]">from {r.proposal!.field_refs[f.id].join(', ')}</span>}
          </span>
          <textarea rows={2} class={textareaCls} aria-label={`${f.label} for ${r.name}`} value={text[f.id]} disabled={approved}
            onInput={(e) => setText({ ...text, [f.id]: (e.target as HTMLTextAreaElement).value })} />
          {reviews[f.id] && !teacherFields.has(f.id) && <span class="text-[11.5px] text-[#b42318]">Rewrite: {reviews[f.id]}</span>}
          {warn[f.id] && <span class="text-[11.5px] text-[#b45309]">{warn[f.id]}</span>}
        </label>
      ))}
      {!approved && (
        <div class="flex flex-wrap items-center gap-2">
          <Btn disabled={busy || !dirty} onClick={save}>Save edits</Btn>
          <Btn kind="primary" disabled={busy || dirty || !!r.approve_block} onClick={approve}>Approve</Btn>
          {r.approve_block && <span class="text-[12px] text-[var(--color-text-muted)]">Cannot approve yet: {r.approve_block}</span>}
          {dirty && <span class="text-[12px] text-[var(--color-text-muted)]">Save your edits first.</span>}
        </div>
      )}
      {approved && <div class="text-[12px] text-[var(--color-text-muted)]">Approved by {r.feedback?.approved_by}.</div>}
      <ErrorLine error={err} />
      {!approved && <NotApplicable ctx={ctx} r={r} />}
    </div>
  );
}

/** U16: feedback not applicable is its own teacher action with a reason. */
function NotApplicable({ ctx, r }: { ctx: Ctx; r: FeedbackRowView }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function save() {
    setErr(null); setBusy(true);
    try {
      await api('POST', `/api/t/assessments/${ctx.a}/feedback/${r.student_id}/not-applicable`, { reason, expectedRevision: r.feedback?.feedback_revision ?? 0 });
      await ctx.reload();
    } catch (e) { setErr(errText(e)); }
    setBusy(false);
  }
  if (!open) return <div><button type="button" class="text-[11.5px] text-[var(--color-text-faint)] hover:text-[var(--color-text)]" onClick={() => setOpen(true)}>No feedback needed for this student…</button></div>;
  return (
    <div class="flex flex-col gap-1">
      <div class="flex flex-wrap items-center gap-1.5">
        <input class={inputCls + ' flex-1 min-w-[160px]'} aria-label={`Why feedback is not applicable for ${r.name}`} placeholder="Reason (required)" value={reason} onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
        <Btn disabled={busy || !reason.trim()} onClick={save}>Record not applicable</Btn>
        <Btn onClick={() => setOpen(false)}>Cancel</Btn>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}
